import React, { useState } from 'react';
import LanguageSelector from './LanguageSelector';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../supabaseClient';
import { isTextSafe } from '../utils/safeForAds';
import { backendUrl } from '../config';
import './CreateClassroomModuleModal.css';

const CreateClassroomModuleModal = ({ classroomId, classroomName, onClose, onSuccess }) => {
  const { t } = useLanguage();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCreate = async (from, to, dialect, name) => {
    if (dialect && dialect.length > 50) {
      setError(t('dialectMaxLength') || 'Dialect cannot exceed 50 characters');
      return;
    }
    // Check for profanity in dialect
    if (dialect && !isTextSafe(dialect.trim())) {
      setError(t('inappropriateContentDetected') || 'Inappropriate content detected. Please use appropriate language.');
      return;
    }
    if (!name || !name.trim()) {
      setError(t('moduleNameRequired') || 'Module name is required');
      return;
    }
    if (name.trim().length > 150) {
      setError(t('moduleNameMaxLength') || 'Module name cannot exceed 150 characters');
      return;
    }
    // Check for profanity in module name
    if (!isTextSafe(name.trim())) {
      setError(t('inappropriateContentDetected') || 'Inappropriate content detected. Please use appropriate language.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      // Get user session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        setError(t('pleaseLogIn'));
        setLoading(false);
        return;
      }

      // Call backend to create module (this handles subscription check)
      const response = await fetch(`${backendUrl}/create-classroom-module`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          classroomId: classroomId,
          fromLang: from,
          toLang: to,
          dialect: dialect,
          name: name.trim(),
          accessToken: session.access_token
        })
      });

      const responseText = await response.text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseErr) {
        console.error('Failed to parse response as JSON:', responseText);
        throw new Error(`Server returned non-JSON response: ${responseText.substring(0, 100)}...`);
      }

      if (!response.ok) {
        if (data.code === 'CLASSROOM_EXPIRED') {
          setError(t('classroomSubscriptionExpired') || 'This classroom subscription has expired. Please renew to create new modules.');
        } else {
          setError(data.error || t('couldNotCreateModule'));
        }
        setLoading(false);
        return;
      }

      // Success - close modal and refresh
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error creating classroom module:', err);
      setError(err.message || t('couldNotCreateModule') || 'Could not create module.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('createClassroomModule') || 'Create Module'} - {classroomName}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {error && (
          <div className="modal-error">
            {error}
          </div>
        )}

        {loading && (
          <div className="modal-loading">
            {t('creating') || 'Creating...'}
          </div>
        )}

        <LanguageSelector onSubmit={handleCreate} onCancel={handleCancel} showDialect />
      </div>
    </div>
  );
};

export default CreateClassroomModuleModal;

